import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faRotateLeft, faRightToBracket } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../context/AuthContext";
import styles from "./learningStatus.module.scss";
import { useLocalizedPath } from "../../i18n/localizedPath";

interface LearningStatusButtonProps {
    learned: boolean;
    disabled?: boolean;
    onClick: () => void;
}

// Progress tracking is an account feature, so a signed-out visitor is offered
// the way in rather than a control that would silently do nothing. Rendered
// here rather than at each call site so no page can forget the gate.
export function SignInToTrackLink({ label }: { label?: string }) {
    const lp = useLocalizedPath();
    const { t } = useTranslation("common");
    const location = useLocation();
    return (
        <Link to={lp("/login")} state={{ from: location }} className={`${styles.button} ${styles.buttonLink}`}>
            <FontAwesomeIcon icon={faRightToBracket} />
            {label ?? t("learningStatus.signInToTrack")}
        </Link>
    );
}

export function LearningStatusButton({ learned, disabled = false, onClick }: LearningStatusButtonProps) {
    const { t } = useTranslation("common");
    const { status } = useAuth();

    if (status === "unauthenticated") return <SignInToTrackLink />;
    // Once something is learned the button's job is to undo that, so it is
    // labelled with the action it performs rather than the state it is in.
    // The state itself is carried by LearnedBadge, which every page renders.
    // That also means it is no longer a toggle button: aria-pressed requires a
    // label that stays put, and this one changes.
    return (
        <button
            type="button"
            className={`${styles.button} ${learned ? styles.buttonLearned : ""}`}
            onClick={onClick}
            disabled={disabled}
        >
            {learned && <FontAwesomeIcon icon={faRotateLeft} />}
            {disabled ? t("learningStatus.syncing") : learned ? t("learningStatus.reviewAgain") : t("learningStatus.markAsLearned")}
        </button>
    );
}

export function LearnedBadge({ label }: { label?: string }) {
    const { t } = useTranslation("common");
    const resolvedLabel = label ?? t("learningStatus.learned");
    return (
        <span className={styles.badge} aria-label={label ?? t("learningStatus.markedAsLearned")}>
            <FontAwesomeIcon icon={faCheck} />
            <span>{resolvedLabel}</span>
        </span>
    );
}
